import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { AppRoutes } from '@nimbus-assistant/enums/app-routes';
import { TaskState } from '@nimbus-assistant/models/task.model';
import { AssistantTasksService } from '@nimbus-assistant/services/assistant-tasks.service';
import { TaskToastService } from '@nimbus-assistant/services/task-toast.service';
import { ITaskTab } from '@nimbus-assistant/models/task-details-tabs';
import { SharedUIModule } from '@nimbus-library/features/shared/shared.ui.module';
import { BannerComponent } from './banner/banner.component';
import { FooterComponent } from './footer/footer.component';
import { TaskIncomingOverlayComponent } from './task-incoming-overlay/task-incoming-overlay.component';
import { TaskFlowTabComponent } from './task-details/task-flow-tab/task-flow-tab.component';
import { TaskContextTabComponent } from './task-details/task-context-tab/task-context-tab.component';

@UntilDestroy()
@Component({
  standalone: true,
  selector: 'app-shell',
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterOutlet,
    TranslateModule,
    SharedUIModule,
    BannerComponent,
    FooterComponent,
    TaskIncomingOverlayComponent,
    TaskFlowTabComponent,
    TaskContextTabComponent,
  ],
})
export class ShellComponent {
  private static readonly TASK_ROUTES = [AppRoutes.home, AppRoutes.call];

  public readonly showIncomingOverlay$ = combineLatest([this.tasksService.connectedTask$, this.tasksService.incomingTask$]).pipe(
    map(([connected, incoming]) => !!connected && !!incoming),
  );

  public readonly incomingTask$ = this.tasksService.incomingTask$;
  public readonly incomingTaskLive$ = this.tasksService.tasks$.pipe(
    map((tasks) => tasks.find((t) => t.state === TaskState.Ringing) ?? null),
  );
  public readonly isExtendingTeamsToast$ = this.taskToastService.isExtendingTeamsToast$;

  public readonly showBanner$ = new BehaviorSubject<boolean>(false);
  public readonly showFooter$ = new BehaviorSubject<boolean>(false);

  public toastTabItems: ITaskTab[] = null;

  public constructor(
    private readonly tasksService: AssistantTasksService,
    private readonly taskToastService: TaskToastService,
    private readonly router: Router,
    private readonly cd: ChangeDetectorRef,
  ) {
    this.taskToastService.extendedTabItems$.pipe(untilDestroyed(this)).subscribe((tabs) => {
      this.toastTabItems = tabs;
      this.cd.markForCheck();
    });

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        untilDestroyed(this),
      )
      .subscribe((e: NavigationEnd) => {
        const onTaskRoute = ShellComponent.TASK_ROUTES.some((r) => e.url.endsWith(r));
        this.showBanner$.next(onTaskRoute);
        this.showFooter$.next(onTaskRoute);
      });
  }

  public navigateToastTab(id: string): void {
    this.toastTabItems?.forEach((tab: ITaskTab) => (tab.isActive = tab.id === id));
  }

  public isToastTabActive(id: string): boolean {
    return this.toastTabItems?.find((tab: ITaskTab) => tab.id === id)?.isActive;
  }
}
